import InformationalDialog from 'components/dialogs/InformationalDialog';
import TextInput from 'components/form/TextInput';
import TextAreaInput from 'components/form/TextAreaInput';
import { AuLicenseesCountry, EUCountries, CurrencyType, REG_URL, Role } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { Field, Formik, useFormikContext } from 'formik';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import CompanyService from 'services/CompanyService';
import CountriesService from 'services/CountriesService';
import { CompanySize, CountryModel, CreateCompanyModel, CurrencySelectModel, LoginModel } from 'services/EpdClient';
import styled, { useTheme } from 'styled-components';
import {
  A,
  ButtonSmall,
  Container,
  ErrorText,
  FormMode,
  StyledForm,
  StyledReactSelect,
  StyledLink,
  SuccessText,
  Text,
} from 'styles/Styles.styled';
import { PoorMansError } from 'util/utils';
import * as Yup from 'yup';
import { FieldPanel, GrayHR } from '../../styles/v2/Styles.styled';
import { Theme } from 'styles/theme';
import { YES_NO, YesNoDataSource } from '../../components/v2/compiler/constants';
import { Option } from 'types/types';
import AvailableCountryCurrencies from '../../components/company/AvailableCountryCurrencies';
import { useDictionaryValues } from 'services/api/queries';
import calculateCurrencyOptions from 'components/company/calculateCurrencyOptions';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { registerCompanySelectStyles } from 'components/form/EditSelectStyles';
import { DropdownIndicator } from 'components/form/DropdownIndicator';
import VatValidationService from 'services/VatValidationService';
import { useGetRegexVATPattern } from 'components/company/useGetRegexVATPattern';

type ExtendedCreateCompanyModel = CreateCompanyModel &
  CurrencySelectModel & {
    marketplace: Option[] | null;
    roleOption: Option;
    companySizeOption: Option;
    countryOption: Option | null;
    isInvoiceUniquePurchaseOrderNumberOption: Option | null;
    isSpecialInvoiceNeedsOption: Option | null;
  };

const RegisterCompany: FunctionComponent = () => {
  const theme = useTheme() as Theme;

  const { company, licensee, changeCompany } = useContext(CompanyContext);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const [showCreatedLicenseeCompanyDialog, setShowCreatedLicenseeCompanyDialog] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string>();
  const [countries, setCountries] = useState<CountryModel[]>([]);
  const [countriesDs, setCountriesDs] = useState<Option[]>([]);
  const [isGlobalMarketplaceSelected, setIsGlobalMarketplaceSelected] = useState(false);
  const companyTypes = [
    { value: Role.EPDOwner, label: 'EPD Owner' },
    { value: Role.EPDDeveloper, label: 'LCA Practitioner' },
  ] as Option[];
  const glnValidationRegex = /^\d{13}$/;
  const { t } = useTranslation();
  const eurCurrencyCountries = useDictionaryValues(EPD_DICTIONARY_CODES.EUR_CURRENCY_COUNTRIES);
  const EU_COUNTRIES = [
    'Austria',
    'Belgium',
    'Bulgaria',
    'Croatia',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Estonia',
    'Finland',
    'France',
    'Germany',
    'Greece',
    'Hungary',
    'Ireland',
    'Italy',
    'Latvia',
    'Lithuania',
    'Luxembourg',
    'Malta',
    'The Netherlands',
    'Poland',
    'Portugal',
    'Romania',
    'Slovakia',
    'Slovenia',
    'Spain',
    'Sweden',
  ];
  const isEUCountry = (countryName: string): boolean => EU_COUNTRIES.includes(countryName);
  const createInitialValues: ExtendedCreateCompanyModel = {
    name: '',
    roleId: Role.EPDOwner,
    website: '',
    countryId: '',
    vat: '',
    companySize: CompanySize.Missing,
    organizationEmail: '',
    isOnlyEmail: false,
    addressLine: '',
    addressLineSecond: '',
    addressCity: '',
    addressZipCode: '',
    isCompanyAddressUsedForInvoice: false,
    invoiceOrganizationName: '',
    invoiceCountryId: '',
    invoiceAddressLine: '',
    invoiceAddressLineSecond: '',
    invoiceAddressCity: '',
    invoiceAddressZipCode: '',
    invoiceEmail: '',
    invoiceCcEmail: '',
    ordersEmail: '',
    remindersEmail: '',
    isInvoiceUniquePurchaseOrderNumber: YES_NO.NO,
    isSpecialInvoiceNeeds: YES_NO.NO,
    specialInvoiceNeeds: '',
    invoiceReference: '',
    isGlobalMarketplace: false,
    marketplace: [],
    preferredCurrency: null,
    gln: '',
    abn: '',
    gst: '',
    roleOption: { value: Role.EPDOwner, label: companyTypes.find((x) => x.value == Role.EPDOwner)?.label ?? '' } as Option,
    companySizeOption: { value: CompanySize.Missing, label: CompanySize.Missing } as Option,
    countryOption: null,
    preferredCurrencyOption: null,
    invoiceCountryOption: null,
    isInvoiceUniquePurchaseOrderNumberOption: {
      value: YES_NO.NO,
      label: YesNoDataSource.find((x) => x.value == YES_NO.NO)?.label ?? '',
    },
    isSpecialInvoiceNeedsOption: { value: YES_NO.NO, label: YesNoDataSource.find((x) => x.value == YES_NO.NO)?.label ?? '' },
  };

  const createSchema = Yup.object({
    name: Yup.string().required(t('messages.required')),
    roleOption: Yup.object<Option>().required(t('messages.required')),
    website: Yup.string().matches(REG_URL, t('messages.invalidUrl')).required(t('messages.required')),
    countryOption: Yup.object<Option>().required(t('messages.required')),
    addressLine: Yup.string().required(t('messages.required')),
    addressCity: Yup.string().required(t('messages.required')),
    addressZipCode: Yup.string().required(t('messages.required')),

    vat: Yup.mixed().when(['invoiceCountryOption', 'roleOption'], {
      is: (invoiceCountryOption: Option | null, roleOption: Option | null) => {
        if (roleOption?.value !== Role.EPDOwner || !invoiceCountryOption?.value) return false;
        const country = countries.find((x) => x.id === invoiceCountryOption.value);
        return isEUCountry(country?.name || '');
      },
      then: Yup.string()
        .required(t('messages.requiredVatInEU'))
        .test('vat-format', 'Invalid VAT format', function (value) {
          if (!value) return true;

          const country = countries.find((x) => x.id === this.parent.invoiceCountryOption?.value);

          if (!country || !country.name) return true;

          const vatPattern = useGetRegexVATPattern(country.name);
          return vatPattern?.test(value) ?? true;
        })
        .test('vat-validation', 'The VAT number entered is invalid. Please check and try again.', async function (value) {
          if (!value) return false;

          const country = countries.find((x) => x.id === this.parent.invoiceCountryOption?.value);
          if (!country || !country.name) return false;

          const vatPattern = useGetRegexVATPattern(country.name);
          if (vatPattern && !vatPattern.test(value)) {
            return true;
          }

          if (!isEUCountry(country.name)) {
            return true;
          }

          try {
            const result = await VatValidationService.get(value);
            return result.validationStatus !== 'InvalidVat';
          } catch (error) {
            console.warn('VAT validation service error:', error);
            return true;
          }
        }),
    }),
    companySizeOption: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.object<Option>().required(t('messages.required')),
    }),
    organizationEmail: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.string().email(t('messages.invalidEmail')).required(t('messages.required')),
    }),
    invoiceOrganizationName: (Yup.string() as any).when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.string().required(t('messages.required')),
    }),
    invoiceAddressLine: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.string().required(t('messages.required')),
    }),
    invoiceAddressCity: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.string().required(t('messages.required')),
    }),
    invoiceAddressZipCode: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.string().required(t('messages.required')),
    }),
    invoiceCountryOption: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.object<Option>().required(t('messages.required')),
    }),
    ordersEmail: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.string().email(t('messages.invalidEmail')).required(t('messages.required')),
    }),
    remindersEmail: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.string().email(t('messages.invalidEmail')).required(t('messages.required')),
    }),
    invoiceEmail: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.string().email(t('messages.invalidEmail')).required(t('messages.required')),
    }),
    invoiceCcEmail: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.string()
        .nullable()
        .email(t('messages.invalidEmail'))
        .test('invoice-email-match', t('messages.ccInvoiceEmailIsTheSameAsInvoiceEmail'), function (value) {
          return this.parent.invoiceEmail !== value;
        }),
    }),
    specialInvoiceNeeds: Yup.mixed().when(['roleOption', 'isSpecialInvoiceNeedsOption'], {
      is: (roleOption: Option | null, isSpecialInvoiceNeedsOption: Option | null) =>
        roleOption?.value === Role.EPDOwner && isSpecialInvoiceNeedsOption?.value === YES_NO.YES,
      then: Yup.string().required(t('messages.required')),
    }),
    marketplace: Yup.array().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDDeveloper,
      then: Yup.array().min(1, t('messages.required')),
    }),
    gln: (Yup.string() as any)
      .when([], {
        is: (invoiceCountryOption: Option | null) => {
          if (!invoiceCountryOption) return false;
          const countryName = countries.find((c) => c.id === invoiceCountryOption.value)?.name;
          return countryName == 'Sweden';
        },
        then: Yup.string().matches(glnValidationRegex as RegExp, 'Invalid GLN format') as Yup.StringSchema,
      })
      .nullable(),
    preferredCurrencyOption: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.object<Option>().required(t('messages.required')),
    }),
    abn: Yup.mixed()
      .when(['countryOption', 'roleOption'], {
        is: (countryOption: Option | undefined, roleOption: Option | undefined) =>
          roleOption?.value === Role.EPDOwner &&
          countryOption &&
          AuLicenseesCountry.includes(countries.find((x) => x.id === countryOption.value)?.alpha2Code || ''),
        then: Yup.string().required(t('messages.required')),
      })
      .nullable(),
    gst: Yup.mixed()
      .when(['countryOption', 'roleOption'], {
        is: (countryOption: Option | undefined, roleOption: Option | undefined) =>
          roleOption?.value === Role.EPDOwner &&
          countryOption &&
          AuLicenseesCountry.includes(countries.find((x) => x.id === countryOption.value)?.alpha2Code || ''),
        then: Yup.string().required(t('messages.required')),
      })
      .nullable(),
  });

  const createCompany = async (rowValues: ExtendedCreateCompanyModel) => {
    const values: CreateCompanyModel = {
      ...rowValues,
      roleId: rowValues.roleOption?.value,
      companySize: rowValues.companySizeOption.value as CompanySize,
      countryId: rowValues.countryOption?.value,
      invoiceCountryId: rowValues.invoiceCountryOption?.value ?? null,
      preferredCurrency: rowValues.preferredCurrencyOption?.value ?? CurrencyType.EUR,
      countries: rowValues.marketplace?.filter((c) => c.label !== 'Global').map((c) => ({ id: c.value, name: c.label })),
      isInvoiceUniquePurchaseOrderNumber: rowValues.isInvoiceUniquePurchaseOrderNumberOption?.value ?? null,
      isSpecialInvoiceNeeds: rowValues.isSpecialInvoiceNeedsOption?.value ?? null,
    };
    setServerError('');
    if (values.roleId === Role.EPDDeveloper) {
      values.isGlobalMarketplace = isGlobalMarketplaceSelected;
      values.companySize = CompanySize.Missing;
      values.vat = null;
      values.gst = null;
      values.abn = null;
      values.invoiceOrganizationName = null;
      values.invoiceAddressCity = null;
      values.invoiceCountryId = null;
      values.invoiceAddressLine = null;
      values.invoiceAddressLineSecond = null;
      values.invoiceAddressZipCode = null;
      values.invoiceCcEmail = null;
      values.invoiceEmail = null;
      values.invoiceReference = null;
      values.isInvoiceUniquePurchaseOrderNumber = null;
      values.isSpecialInvoiceNeeds = null;
      values.ordersEmail = null;
      values.remindersEmail = null;
      values.specialInvoiceNeeds = null;

      if (isGlobalMarketplaceSelected) {
        values.countries = [];
      }
    } else {
      values.isGlobalMarketplace = false;
      values.countries = [{ id: values.countryId, name: '', alpha2Code: '' }];
      if (values.isSpecialInvoiceNeeds === YES_NO.NO) {
        values.specialInvoiceNeeds = null;
      }
    }
    try {
      const newCompanyId = await CompanyService.registerNewCompany(values);
      setSuccess(true);

      const currentUser = AuthService.getUser().email;
      if (currentUser) {
        changeCompany?.(newCompanyId || '');
        await AuthService.relogin({ userName: currentUser, password: '' } as LoginModel);
      }
    } catch (e) {
      const error = PoorMansError(e);
      setServerError(error ? error : t('errors.wrong'));
    }
  };

  const handleCountryChange = (selectedOption: Option, setFieldValue: any) => {
    if (!eurCurrencyCountries.data) return;
    const country = countries.find((c) => c.id === selectedOption.value);
    if (!country) return;
    const countryCode = country.alpha2Code;
    if (!countryCode) {
      setFieldValue('preferredCurrencyOption', null);
      return;
    }

    const options = calculateCurrencyOptions(
      countryCode,
      eurCurrencyCountries.data.map((c) => c.value)
    );

    setFieldValue('preferredCurrencyOption', options[0]);

    return selectedOption;
  };

  const handleChangeMarketplace = (options: Option[]) => {
    const globalOption = options?.find((option) => option.label === 'Global');
    if (globalOption) {
      setIsGlobalMarketplaceSelected(true);
      return [globalOption];
    } else {
      setIsGlobalMarketplaceSelected(false);
      return options;
    }
  };

  const CopyEmailToInvoiceEmail = () => {
    const { values, setFieldValue } = useFormikContext<typeof createInitialValues>();

    useEffect(() => {
      if (values.isOnlyEmail) {
        setFieldValue('invoiceEmail', values.organizationEmail);
        setFieldValue('ordersEmail', values.organizationEmail);
        setFieldValue('remindersEmail', values.organizationEmail);
      }
    }, [values.isOnlyEmail, values.organizationEmail, setFieldValue]);

    return null;
  };

  const CopyAddressToInvoiceAddress = () => {
    const { values, setFieldValue } = useFormikContext<typeof createInitialValues>();

    useEffect(() => {
      if (values.isCompanyAddressUsedForInvoice) {
        setFieldValue('invoiceAddressCity', values.addressCity);
        setFieldValue('invoiceAddressLine', values.addressLine);
        setFieldValue('invoiceAddressLineSecond', values.addressLineSecond);
        setFieldValue('invoiceAddressZipCode', values.addressZipCode);
        setFieldValue('invoiceCountryOption', values.countryOption);
        const country = countries.find((c) => c.id === values.countryOption?.value);
        const countryCode = country?.alpha2Code;
        if (!eurCurrencyCountries.data || !countryCode) return;
        const options = calculateCurrencyOptions(
          countryCode,
          eurCurrencyCountries.data.map((c) => c.value)
        );
        setFieldValue('preferredCurrencyOption', options[0]);
      }
    }, [values.isCompanyAddressUsedForInvoice, setFieldValue]);

    return null;
  };

  useEffect(() => {
    async function fetch() {
      const result = await CountriesService.getCountries();
      const emptyCountry: CountryModel = { id: undefined, name: '', alpha2Code: '' };
      const globalMarket: Option = { value: '', label: 'Global' };
      setCountries([emptyCountry].concat(result));
      setCountriesDs(
        [globalMarket].concat(
          result.map((country) => {
            return { value: country.id, label: country.name } as Option;
          })
        )
      );
    }
    fetch();
  }, []);

  useEffect(() => {
    if (success && licensee) {
      setShowCreatedLicenseeCompanyDialog(true);
    }
  }, [licensee]);

  return (
    <Container style={{ marginTop: 0, paddingLeft: 0 }}>
      <Text dangerouslySetInnerHTML={{ __html: t('organization.register.description') }} />
      <Formik initialValues={createInitialValues} validationSchema={createSchema} onSubmit={createCompany}>
        {({ isSubmitting, values, errors, setFieldValue }) => (
          <RegisterCompanyForm mode={FormMode.Inline}>
            <Text style={{ fontWeight: 'bold', marginTop: 30 }}> {t('organization.register.general')}</Text>
            <FieldPanel>
              <Grid>
                <StyledReactSelect
                  required
                  label={t('organization.type')}
                  name="roleOption"
                  styles={registerCompanySelectStyles(theme)}
                  placeholder={t('placeholder.select')}
                  options={companyTypes}
                  enableTooltipError
                  supressErrors
                  components={{ DropdownIndicator }}
                />
              </Grid>
              <Grid>
                <TextInput
                  required
                  label={t('organization.name')}
                  name="name"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors
                />
              </Grid>

              {values.roleOption?.value === Role.EPDOwner && (
                <>
                  <Grid>
                    <StyledReactSelect
                      required
                      label={t('organizationSize')}
                      name="companySizeOption"
                      placeholder={t('placeholder.select')}
                      styles={registerCompanySelectStyles(theme)}
                      options={[''].concat(Object.values(CompanySize)).map((x) => {
                        return { value: x, label: x } as Option;
                      })}
                      enableTooltipError
                      supressErrors
                      components={{ DropdownIndicator }}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('website')}
                      name="website"
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      name="organizationEmail"
                      label={t('organizationEmail')}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                    <CheckBoxGroup>
                      <Field type="checkbox" name="isOnlyEmail" id="isOnlyEmailId" />
                      <label htmlFor="isOnlyEmailId">{t(`organization.register.isOnlyEmail`)}</label>
                    </CheckBoxGroup>
                  </Grid>
                </>
              )}

              {values.roleOption?.value === Role.EPDDeveloper && (
                <>
                  <Grid>
                    <TextInput
                      required
                      label={t('website')}
                      name="website"
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <StyledReactSelect
                      placeholder={t('placeholder.select')}
                      label={t('organization.register.marketplace')}
                      name="marketplace"
                      isMulti
                      styles={registerCompanySelectStyles(theme)}
                      options={countriesDs}
                      onChange={handleChangeMarketplace}
                      isClearable
                      shouldMenuOpen={!isGlobalMarketplaceSelected}
                      required
                      enableTooltipError
                      supressErrors
                      components={{ DropdownIndicator }}
                    />
                  </Grid>
                </>
              )}
            </FieldPanel>

            <div className="w-full">
              <GrayHR />
            </div>

            {/* Organization address */}
            <Text style={{ fontWeight: 'bold' }}>Organisation address</Text>
            <FieldPanel>
              <Grid>
                <StyledReactSelect
                  placeholder={t('placeholder.select')}
                  required
                  label={t('country')}
                  name="countryOption"
                  styles={registerCompanySelectStyles(theme)}
                  options={countries.map((c) => {
                    return { value: c.id, label: c.name } as Option;
                  })}
                  enableTooltipError
                  supressErrors
                  components={{ DropdownIndicator }}
                />
              </Grid>
              <Grid>
                <TextInput
                  required
                  label={t('organizationAddressZipCode')}
                  name="addressZipCode"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors
                />
              </Grid>
              <Grid>
                <TextInput
                  required
                  label={t('organizationAddressCity')}
                  name="addressCity"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors
                />
              </Grid>
              <Grid>
                <TextInput
                  required
                  label={t('placeholder.address.line1')}
                  name="addressLine"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors
                />
                {values.roleOption?.value === Role.EPDOwner && (
                  <>
                    <CheckBoxGroup>
                      <Field type="checkbox" name="isCompanyAddressUsedForInvoice" id="isCompanyAddressUsedForInvoiceId" />
                      <label htmlFor="isCompanyAddressUsedForInvoiceId">
                        {t(`organization.register.useCompanyAddressForInvoicing`)}
                      </label>
                    </CheckBoxGroup>
                  </>
                )}
              </Grid>
              <Grid
                style={
                  AuLicenseesCountry.includes(
                    countries.find((x) => x.id === values.countryOption?.value)?.alpha2Code || ''
                  ) || values.roleOption?.value !== Role.EPDOwner
                    ? {}
                    : { marginTop: '-20px' }
                }
              >
                <TextInput
                  label={t('placeholder.address.line2')}
                  name="addressLineSecond"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors
                />
              </Grid>
              {values.roleOption?.value === Role.EPDOwner &&
                AuLicenseesCountry.includes(
                  countries.find((x) => x.id === values.countryOption?.value)?.alpha2Code || ''
                ) && (
                  <>
                    <Grid>
                      <TextInput
                        required
                        label={t('abn')}
                        name="abn"
                        placeholder={t('placeholder.typeHere')}
                        enableTooltipError
                        supressErrors
                      />
                    </Grid>
                    <Grid>
                      <TextInput
                        required
                        label={t('gst')}
                        name="gst"
                        placeholder={t('placeholder.typeHere')}
                        enableTooltipError
                        supressErrors
                      />
                    </Grid>
                  </>
                )}
            </FieldPanel>

            {values.roleOption?.value === Role.EPDOwner && (
              <>
                <div className="w-full">
                  <GrayHR />
                </div>

                <Text style={{ fontWeight: 'bold' }}>{t('invoiceInfo.title')}</Text>
                <FieldPanel>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceOrganizationName')}
                      name="invoiceOrganizationName"
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <StyledReactSelect
                      required
                      label={t('invoiceAddressCountry')}
                      name="invoiceCountryOption"
                      readOnly={values.isCompanyAddressUsedForInvoice}
                      onChange={(value: Option) => handleCountryChange(value, setFieldValue)}
                      styles={registerCompanySelectStyles(theme)}
                      placeholder={t('placeholder.select')}
                      options={countries.map((c) => {
                        return { value: c.id, label: c.name } as Option;
                      })}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <AvailableCountryCurrencies
                      countries={countries}
                      styles={registerCompanySelectStyles(theme)}
                      tooltip={!values.invoiceCountryOption ? t('invoicePreferredCurrencyTooltip') : ''}
                      enableTooltipError
                      supressErrors
                      isRequired={true}
                      isDisabled={!values.invoiceCountryOption}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceAddressZipCode')}
                      name="invoiceAddressZipCode"
                      readOnly={values.isCompanyAddressUsedForInvoice}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceAddressCity')}
                      name="invoiceAddressCity"
                      readOnly={values.isCompanyAddressUsedForInvoice}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceAddressLine')}
                      name="invoiceAddressLine"
                      readOnly={values.isCompanyAddressUsedForInvoice}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      label={t('invoiceAddressLineSecond')}
                      name="invoiceAddressLineSecond"
                      readOnly={values.isCompanyAddressUsedForInvoice}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoicingEmail')}
                      name="invoiceEmail"
                      readOnly={values.isOnlyEmail}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      enableTooltipError
                      supressErrors
                      label={t('invoiceCcEmail')}
                      name="invoiceCcEmail"
                      placeholder={t('placeholder.typeHere')}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceOrderingEmail')}
                      name="ordersEmail"
                      readOnly={values.isOnlyEmail}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceRemindingEmail')}
                      name="remindersEmail"
                      readOnly={values.isOnlyEmail}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                  {EUCountries.includes(
                    countries.find((x) => x.id === values.invoiceCountryOption?.value)?.alpha2Code || ''
                  ) && (
                    <Grid>
                      <TextInput
                        label={t('vat')}
                        name="vat"
                        required={true}
                        placeholder={t('placeholder.typeHere')}
                        enableTooltipError
                        supressErrors
                      />
                    </Grid>
                  )}

                  <Grid>
                    <StyledReactSelect
                      required
                      label={t('isSpecialInvoice')}
                      name="isSpecialInvoiceNeedsOption"
                      styles={registerCompanySelectStyles(theme)}
                      placeholder={t('placeholder.select')}
                      options={YesNoDataSource.map((x) => {
                        return { value: x.value, label: x.label } as Option;
                      })}
                      enableTooltipError
                      supressErrors
                      components={{ DropdownIndicator }}
                    />
                  </Grid>
                  <Grid>
                    <StyledReactSelect
                      required
                      label={t('isUniqueInvoicePurchaseOrderNumberRequired')}
                      name="isInvoiceUniquePurchaseOrderNumberOption"
                      styles={registerCompanySelectStyles(theme)}
                      placeholder={t('placeholder.select')}
                      options={YesNoDataSource.map((x) => {
                        return { value: x.value, label: x.label } as Option;
                      })}
                      enableTooltipError
                      supressErrors
                      components={{ DropdownIndicator }}
                    />
                  </Grid>
                  {values.isSpecialInvoiceNeedsOption?.value === YES_NO.YES && (
                    <>
                      <Grid>
                        <TextInput
                          required
                          label={t('organization.register.specialInvoiceNeeds')}
                          name="specialInvoiceNeeds"
                          placeholder={t('placeholder.typeHere')}
                          enableTooltipError
                          supressErrors
                        />
                      </Grid>
                    </>
                  )}
                  {values.invoiceCountryOption?.value &&
                    countries.find((c) => c.id === values.invoiceCountryOption?.value)?.name === 'Sweden' && (
                      <>
                        <Grid>
                          <TextInput
                            label={t('gln')}
                            name="gln"
                            placeholder={t('placeholder.typeHere')}
                            enableTooltipError
                            supressErrors
                          />
                        </Grid>
                      </>
                    )}
                  <Grid style={{ gridColumn: 'span 2' }}>
                    <TextAreaInput
                      label={t('invoiceReference')}
                      name="invoiceReference"
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors
                    />
                  </Grid>
                </FieldPanel>
              </>
            )}
            <ButtonGroup>
              <ButtonSmall type="submit" disabled={isSubmitting || success}>
                {isSubmitting ? t('organization.register.creating') : t('organization.register.create')}
              </ButtonSmall>
              {success && (
                <SuccessText>
                  <div>{t('organization.register.success')}</div>
                  <StyledLink to={EpdLinks.dashboard()}>{t('organization.register.goToDashboard')}</StyledLink>
                </SuccessText>
              )}
              {serverError && <ErrorText>{serverError}</ErrorText>}
            </ButtonGroup>
            <CopyEmailToInvoiceEmail />
            <CopyAddressToInvoiceAddress />
          </RegisterCompanyForm>
        )}
      </Formik>
      <br />
      <InformationalDialog
        isOpened={showCreatedLicenseeCompanyDialog && !!licensee?.localHomepage}
        header="Regional Licensee country is detected"
        onHide={() => setShowCreatedLicenseeCompanyDialog(false)}
      >
        Thank you for registering with EPD International. We have various Licensees around the world. You have selected a
        country located in the regional license holder {company?.address?.country}. The regional administrators will help you
        with all support regarding your EPD. To find the local support, please visit{' '}
        <LicenseeSiteLink href={licensee?.localHomepage}>{licensee?.localHomepage}</LicenseeSiteLink>
      </InformationalDialog>
    </Container>
  );
};

export const RegisterCompanyForm = styled(StyledForm)`
  ${FieldPanel} {
    box-shadow: none;
    align-items: flex-start;
    padding-left: 0;
    padding-bottom: 0;
  }

  ${GrayHR} {
    margin: 30px 30px 40px 0;
  }

  input {
    ${(props) => props.theme.fonts.textSmall}
  }
`;

const LicenseeSiteLink = styled(A)`
  color: ${(props) => props.theme.colors.primaryOrange};
`;

const Grid = styled.div`
  display: grid;

  ${StyledReactSelect} {
    margin: 0.5rem 0 1rem;
  }

  label {
    ${(props) => props.theme.fonts.textSmall}
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 50px;
  padding-right: 30px;
  min-height: 55px;

  ${ButtonSmall} {
    align-self: center;
    margin-right: 2rem;
    height: 48px;
  }

  ${SuccessText} {
    margin-top: 0;
    margin-bottom: 0;

    & div {
      margin-bottom: 1rem;
    }
  }
`;

const CheckBoxGroup = styled.div`
  display: flex;
  align-items: center;
  min-height: 20px;
  & .p-checkbox {
    width: 14px;
    height: 14px;
  }
  & .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
    border: 1px solid ${(props) => props.theme.colors.elementsButtonDisabled};
    border-radius: 0;
  }
  & .p-checkbox .p-checkbox-box.p-highlight {
    background: ${(props) => props.theme.colors.primaryOrange};
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }
  & .p-checkbox .p-checkbox-box.p-highlight:hover {
    background: ${(props) => props.theme.colors.primaryOrange};
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }
  && .p-checkbox-box.p-focus {
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }

  label {
    display: flex;
    margin-left: 0.5rem;
    ${(props) => props.theme.fonts.textSmall}
    color: ${(props) => props.theme.colors.primaryBlack};
  }
`;

export default RegisterCompany;
